import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { fr, type TranslationKeys } from './locales/fr';
import { en } from './locales/en';

// Supported languages
export type Language = 'fr' | 'en';

// All translations (en may have slight differences, cast for compatibility)
const translations: Record<Language, TranslationKeys> = { fr, en: en as TranslationKeys };

// Context type
interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
}

// Create context
const I18nContext = createContext<I18nContextType | null>(null);

// Storage key
const STORAGE_KEY = 'aqua-rush-language';

// Get initial language from localStorage or browser preference
function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'fr';

  // Check localStorage
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'fr' || stored === 'en') return stored;

  // Check browser preference
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('en')) return 'en';

  // Default to French
  return 'fr';
}

// Provider props
interface I18nProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

// Provider component
export function I18nProvider({ children, defaultLanguage }: I18nProviderProps) {
  const [language, setLanguageState] = useState<Language>(defaultLanguage || 'fr');
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize from storage on mount
  useEffect(() => {
    if (!defaultLanguage) {
      const initialLang = getInitialLanguage();
      setLanguageState(initialLang);
    }
    setIsInitialized(true);
  }, [defaultLanguage]);

  // Set language and persist to localStorage
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, lang);
    }
  }, []);

  // Get translations for current language
  const t = translations[language];

  // Don't render until initialized to prevent flash
  if (!isInitialized && typeof window !== 'undefined') {
    return null;
  }

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
}

// Hook to use translations
export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
}

// Hook to get just the language
export function useLanguage() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useLanguage must be used within an I18nProvider');
  }
  return {
    language: context.language,
    setLanguage: context.setLanguage,
  };
}

// Language toggle component
interface LanguageToggleProps {
  className?: string;
}

export function LanguageToggle({ className = '' }: LanguageToggleProps) {
  const { language, setLanguage } = useLanguage();

  const toggleLanguage = () => {
    setLanguage(language === 'fr' ? 'en' : 'fr');
  };

  return (
    <button
      onClick={toggleLanguage}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-sm font-medium ${className}`}
      aria-label={`Switch to ${language === 'fr' ? 'English' : 'French'}`}
    >
      <span className={language === 'fr' ? 'opacity-100' : 'opacity-50'}>FR</span>
      <span className="text-white/40">|</span>
      <span className={language === 'en' ? 'opacity-100' : 'opacity-50'}>EN</span>
    </button>
  );
}

// Export types
export type { TranslationKeys };
export { fr, en };
